import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { withModal } from 'components/modal/ModalHOC';
import AlertModal from 'components/modal/alert-modal/AlertModal';
import ActionLink from 'components/buttons/action-link/ActionLink';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { BlockTitle } from '../../admin-panel';
import ProfileRejectionModal from '../../profile-rejection-modal/ProfileRejectionModal';

import { changeApprovalStatus } from 'modules/admin/reducers/profilesByApprovalStatus';
import { fetchActiveCampaigns } from 'modules/admin/reducers/admin-active-campaigns';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';
import {
  APPROVAL_STATUS_APPROVED,
  APPROVAL_STATUS_REJECTED,
  AVAILABLE_STATE_TRANSITIONS,
  getApprovalStatus
} from 'utils/profile';

import type {
  ActiveCampaign,
  BrandProfile,
  InfluencerProfile
} from 'types';

import './profile-approval-panel.scss';

applyTranslations(require('./profile-approval-panel.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

type OwnProps = {
  profile: InfluencerProfile,
  onChange: (a: void) => void
};

type StateProps = {
  areInfluencerCampaignsLoading: boolean,
  influencerCampaigns: Array<ActiveCampaign>
};

type DispatchProps = {
  dispatchChangeApprovalStatus: (
    profile: InfluencerProfile | BrandProfile,
    profileType: string,
    status: string,
    externalReason: string,
    internalReason?: string | null
  ) => Promise<any>,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      onConfirm?: () => void,
      onCancel?: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchFetchInfluencerActiveCampaigns: (influencerId: number) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const ActionButtonWithModal = withModal(ActionButton, {
  modalContent: ProfileRejectionModal
});

const LinkWithAlert = withModal(ActionLink, {
  modalContent: AlertModal
});

class ProfileApprovalPanel extends React.Component<Props> {
  static defaultProps = {
    onChange: () => {}
  };

  componentDidMount() {
    const {
      profile,
      dispatchFetchInfluencerActiveCampaigns
    } = this.props;

    dispatchFetchInfluencerActiveCampaigns(profile.id);
  }

  changeApprovalStatus(status: string, internalReason: string = '', externalReason: string = ''): void {
    const {
      profile,
      dispatchChangeApprovalStatus,
      dispatchHideConfirmationModal,
      dispatchShowConfirmationModal,
      onChange
    } = this.props;

    dispatchShowConfirmationModal({
      message: __t('profile_approval_panel.profile_change_approval_status_confirmation'),
      onConfirm: () =>
        dispatchChangeApprovalStatus(profile, 'influencer', status, externalReason, internalReason)
          .then(onChange)
          .then(() => dispatchHideConfirmationModal()),
      onCancel: () =>
        dispatchHideConfirmationModal()
    });
  }

  handleRejection = (internalReason: string, externalReason: string) => {
    this.changeApprovalStatus(APPROVAL_STATUS_REJECTED, internalReason, externalReason);
  };

  handleApproval = () => {
    this.changeApprovalStatus(APPROVAL_STATUS_APPROVED);
  };

  renderApprovalStatus() {
    const { profile } = this.props;
    const status = profile.approvalStatus;
    const classes = classnames(
      'profile-approval-panel__approval',
      `profile-approval-panel__approval--${status}`
    );

    return (
      <span className={classes}>
        {__t(`profile_approval_panel.approval_status.${status}`)}
      </span>
    );
  }

  renderRejectionReason() {
    const { profile } = this.props;
    const status = profile.approvalStatus;

    const autoRejectionReasons = profile.autoRejectReasons
      ?.map(autoRejectionReason => __t(`profile_approval_panel.auto_rejection_reasons.${autoRejectionReason}`))
      .join('<br/>');
    const reason = profile.statusChangeInternalReason || autoRejectionReasons;

    if (status !== APPROVAL_STATUS_REJECTED || !reason) {
      return null;
    }

    return (
      <LinkWithAlert
        className="profile-approval-panel__reason"
        size="normal"
        modalContentProps={{
          align: 'left',
          title: __t('profile_approval_panel.rejection_reason_modal_title'),
          message: reason,
          btnText: __t('collabary_common.action_button.close')
        }}
      >
        {__t('profile_approval_panel.rejection_reason_link')}
      </LinkWithAlert>
    );
  }

  render() {
    const { profile, areInfluencerCampaignsLoading, influencerCampaigns } = this.props;
    const approvalStatus = getApprovalStatus(profile);

    if (!approvalStatus) {
      return null;
    }

    const availableStateTransitions = AVAILABLE_STATE_TRANSITIONS[approvalStatus] || [];

    const canReject = availableStateTransitions.includes(APPROVAL_STATUS_REJECTED);
    const canApprove = availableStateTransitions.includes(APPROVAL_STATUS_APPROVED);
    const buttonWidth = canReject && canApprove ? 'small' : 'normal';

    return (
      <section>
        <BlockTitle>
          {__t('profile_approval_panel.approval_status_title')}
          {this.renderApprovalStatus()}
          {this.renderRejectionReason()}
        </BlockTitle>
        <div className="profile-approval-panel__actions">
          {canApprove && (
            <ActionButton
              className="profile-approval-panel__button"
              width={buttonWidth}
              onClick={this.handleApproval}
            >
              {__t('profile_approval_panel.profile_button_change_to_approved')}
            </ActionButton>
          )}
          {canReject && (
            <ActionButtonWithModal
              className="profile-approval-panel__button"
              disabled={areInfluencerCampaignsLoading}
              width={buttonWidth}
              modalContentProps={{
                influencerCampaigns,
                onRejection: (internalReason, externalReason) =>
                  this.handleRejection(internalReason, externalReason)
              }}
            >
              {__t('profile_approval_panel.profile_button_change_to_rejected')}
            </ActionButtonWithModal>
          )}
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ adminInfluencerActiveCampaigns }): StateProps => ({
  areInfluencerCampaignsLoading: adminInfluencerActiveCampaigns.isLoading,
  influencerCampaigns: adminInfluencerActiveCampaigns.campaigns
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchChangeApprovalStatus: (
    profile: InfluencerProfile | BrandProfile,
    profileType: string,
    status: string,
    externalReason: string,
    internalReason?: string | null
  ) => dispatch(changeApprovalStatus(profile, profileType, status, externalReason, internalReason)),

  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchFetchInfluencerActiveCampaigns: (influencerId: number) => dispatch(fetchActiveCampaigns(influencerId))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileApprovalPanel);
